"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHVACUsageData = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
/**
 * Format a date to YYYY-MM-DD format using local timezone
 * @param date Date to format
 * @returns Formatted date string in local timezone
 */
const formatLocalDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const DEFAULT_DAYS = 7;
const transformHVACData = (hvacData) => {
    const groupedByDate = hvacData.reduce((groups, hourlyData) => {
        const date = hourlyData.date;
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(hourlyData);
        return groups;
    }, {});
    const dailyData = [];
    for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateString = formatLocalDate(date);
        console.log(`Processing date: ${dateString}`);
        const hourlyRecords = groupedByDate[dateString] || [];
        const validRecords = hourlyRecords.filter((record) => record.hvacRollup && record.totalDataPoints > 0);
        if (validRecords.length === 0) {
            dailyData.push({
                label: dateString,
                temperature: undefined,
                humidity: undefined,
                pressure: undefined,
            });
        }
        else {
            const avgTemperature = validRecords.reduce((sum, record) => sum + (record.hvacRollup?.avgTemperature || 0), 0) /
                validRecords.length;
            const avgHumidity = validRecords.reduce((sum, record) => sum + (record.hvacRollup?.avgHumidity || 0), 0) /
                validRecords.length;
            const avgPressure = validRecords.reduce((sum, record) => sum + (record.hvacRollup?.avgPressure || 0), 0) /
                validRecords.length;
            const mostRecentTimestamp = Math.max(...validRecords.map(record => record.createdAt));
            dailyData.push({
                label: dateString,
                temperature: avgTemperature,
                humidity: avgHumidity,
                pressure: avgPressure,
                timestamp: mostRecentTimestamp,
            });
        }
    }
    return dailyData;
};
const useHVACUsageData = ({ sensorId, days = DEFAULT_DAYS, enabled = true, }) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)(null);
    // Calculate date range with local timezone preservation
    const endDate = (0, react_1.useMemo)(() => formatLocalDate(new Date()), []);
    const startDate = (0, react_1.useMemo)(() => {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return formatLocalDate(date);
    }, [days]);
    const fetchLatestSensorEvent = async (sensorId) => {
        try {
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorEventsBySensor,
                variables: {
                    sensorId,
                    limit: 1,
                },
            });
            return result?.data?.getSensorEventsBySensor?.[0];
        }
        catch (error) {
            console.error('Error fetching latest sensor event:', error);
            return undefined;
        }
    };
    const refetch = async () => {
        if (!sensorId || !enabled) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Fetch historical rollup data
            const rollupResult = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: {
                    sensorId,
                    startDate,
                    endDate,
                },
            });
            const rawData = rollupResult?.data?.getSensorUsageRollup;
            const sensorUsageData = rawData?.items || rawData || [];
            // Fetch the latest sensor event
            const latestEvent = await fetchLatestSensorEvent(sensorId);
            // Try to parse values from the latest sensor event
            let latestValues = null;
            if (latestEvent) {
                try {
                    latestValues =
                        (typeof latestEvent.values === 'object' ? latestEvent.values : null) ||
                            (typeof latestEvent.values === 'string' ? JSON.parse(latestEvent.values) : null) ||
                            (latestEvent.rawData ? JSON.parse(latestEvent.rawData) : null);
                }
                catch (_) {
                    // Ignore parse errors
                }
            }
            if (sensorUsageData && Array.isArray(sensorUsageData) && sensorUsageData.length > 0) {
                const transformedData = transformHVACData(sensorUsageData);
                // Update the latest data point with the most recent sensor reading if available
                if (latestValues && transformedData.length > 0) {
                    const today = formatLocalDate(new Date());
                    const todayIndex = transformedData.findIndex(item => item.label === today);
                    if (todayIndex >= 0) {
                        transformedData[todayIndex] = {
                            ...transformedData[todayIndex],
                            temperature: latestValues.temperature || latestValues.avgTemperature || transformedData[todayIndex].temperature,
                            humidity: latestValues.humidity || latestValues.avgHumidity || transformedData[todayIndex].humidity,
                            pressure: latestValues.pressure || latestValues.avgPressure || transformedData[todayIndex].pressure,
                            timestamp: latestEvent.timestamp || Date.now(),
                        };
                    }
                }
                setData(transformedData);
            }
            else if (latestValues) {
                // No rollup data yet, but we have a latest sensor event — show it as today's reading
                const today = formatLocalDate(new Date());
                const todayData = [];
                // Build 7-day array with empty days and today populated from latest event
                for (let i = 6; i >= 0; i--) {
                    const date = new Date();
                    date.setDate(date.getDate() - i);
                    const dateString = formatLocalDate(date);
                    if (dateString === today) {
                        todayData.push({
                            label: dateString,
                            temperature: latestValues.temperature ?? latestValues.avgTemperature ?? undefined,
                            humidity: latestValues.humidity ?? latestValues.avgHumidity ?? undefined,
                            pressure: latestValues.pressure ?? latestValues.avgPressure ?? undefined,
                            timestamp: latestEvent.timestamp || Date.now(),
                        });
                    }
                    else {
                        todayData.push({
                            label: dateString,
                            temperature: undefined,
                            humidity: undefined,
                            pressure: undefined,
                        });
                    }
                }
                setData(todayData);
            }
            else {
                setData([]);
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch HVAC data');
            setData(null);
        }
        finally {
            setLoading(false);
        }
    };
    (0, react_1.useEffect)(() => {
        refetch();
    }, [sensorId, startDate, endDate, enabled]);
    return {
        data,
        loading,
        error,
        refetch,
    };
};
exports.useHVACUsageData = useHVACUsageData;
